import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode"

export default class extends Controller {
  static targets = [
    "searchSection", "searchInput", "loading", "preview", "form",
    "coverImage", "titleDisplay", "authorDisplay", "publisherDisplay",
    "title", "subtitle", "author", "isbn", "coverUrl", "description",
    "pageCount", "publishedDate", "publisher",
    "scannerModal", "scannerContainer", "scannerOverlay", "scannerStatus"
  ]

  connect() {
    this.html5Scanner = null
    this.isScanning = false
    this.stream = null
    this.video = null
    this.barcodeDetector = null
    this.animationId = null
    this.useNativeApi = 'BarcodeDetector' in window
  }

  disconnect() {
    this.stopScanner()
  }

  async openScanner() {
    this.scannerModalTarget.classList.remove("hidden")
    await this.startScanner()
  }

  closeScanner() {
    this.stopScanner()
    this.scannerModalTarget.classList.add("hidden")
  }

  async startScanner() {
    if (this.isScanning) return

    // Try native BarcodeDetector first (better performance)
    if (this.useNativeApi) {
      await this.startNativeScanner()
    } else {
      await this.startHtml5Scanner()
    }
  }

  async startNativeScanner() {
    try {
      this.barcodeDetector = new BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128']
      })

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })

      this.video = document.createElement('video')
      this.video.srcObject = this.stream
      this.video.setAttribute('playsinline', '')
      this.video.setAttribute('autoplay', '')
      this.video.setAttribute('muted', '')
      this.video.style.width = '100%'
      this.video.style.height = 'auto'
      this.video.style.display = 'block'

      this.scannerContainerTarget.innerHTML = ''
      this.scannerContainerTarget.appendChild(this.video)

      await this.video.play()

      this.isScanning = true
      this.scannerStatusTarget.textContent = "Point camera at ISBN barcode"

      // Show overlay
      if (this.hasScannerOverlayTarget) {
        this.scannerOverlayTarget.classList.add("active")
      }

      this.scanFrame()
    } catch (err) {
      await this.startHtml5Scanner()
    }
  }

  async scanFrame() {
    if (!this.isScanning || !this.video) return

    try {
      const barcodes = await this.barcodeDetector.detect(this.video)
      if (barcodes.length > 0) {
        this.onScanSuccess(barcodes[0].rawValue)
        return
      }
    } catch (err) {
      // Ignore detection errors
    }

    this.animationId = requestAnimationFrame(() => this.scanFrame())
  }

  async startHtml5Scanner() {
    try {
      this.html5Scanner = new Html5Qrcode("onboarding-barcode-scanner", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128
        ],
        verbose: false
      })

      this.isScanning = true
      this.scannerStatusTarget.textContent = "Point camera at ISBN barcode"

      const config = {
        fps: 20,
        qrbox: { width: 300, height: 150 },
        aspectRatio: 1.333,
        disableFlip: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      }

      await this.html5Scanner.start(
        { facingMode: "environment" },
        config,
        (decodedText) => this.onScanSuccess(decodedText),
        () => {}
      )
    } catch (err) {
      this.scannerStatusTarget.textContent = "Camera access denied. Please allow camera permissions."
    }
  }

  async stopScanner() {
    this.isScanning = false

    // Hide overlay
    if (this.hasScannerOverlayTarget) {
      this.scannerOverlayTarget.classList.remove("active")
    }

    // Stop native scanner
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.video) {
      this.video.srcObject = null
      this.video = null
    }

    // Stop html5-qrcode scanner
    if (this.html5Scanner) {
      try {
        await this.html5Scanner.stop()
        this.html5Scanner.clear()
      } catch (err) {
        // Ignore
      }
      this.html5Scanner = null
    }
  }

  async onScanSuccess(decodedText) {
    const isbn = decodedText.replace(/[^0-9X]/gi, "")

    if (isbn.length !== 10 && isbn.length !== 13) {
      this.scannerStatusTarget.textContent = `Invalid barcode. Try again.`
      return
    }

    this.scannerStatusTarget.textContent = `Found ISBN: ${isbn}. Looking up...`
    await this.stopScanner()
    this.scannerModalTarget.classList.add("hidden")

    // Set the ISBN in the search input and search
    this.searchInputTarget.value = isbn
    this.performSearch(isbn)
  }

  async search(event) {
    event.preventDefault()
    const query = this.searchInputTarget.value.trim()
    if (!query) return
    this.performSearch(query)
  }

  async performSearch(query) {
    this.showLoading()

    try {
      const response = await fetch(`/onboarding/lookup_isbn?isbn=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.populateBook(data)
      } else {
        this.hideLoading()
        alert("Book not found. Try a different ISBN or title.")
      }
    } catch (error) {
      console.error("Lookup error:", error)
      this.hideLoading()
      alert("Something went wrong. Please try again.")
    }
  }

  populateBook(data) {
    // Set hidden field values
    this.titleTarget.value = data.title || ""
    this.subtitleTarget.value = data.subtitle || ""
    this.authorTarget.value = data.authors?.[0] || data.author || ""
    this.isbnTarget.value = data.isbn_13 || data.isbn_10 || ""
    this.coverUrlTarget.value = data.cover_image_url || data.thumbnail || ""
    this.descriptionTarget.value = data.description || ""
    this.pageCountTarget.value = data.page_count || ""
    this.publishedDateTarget.value = data.published_date || ""
    this.publisherTarget.value = data.publisher || ""

    // Update preview display
    this.titleDisplayTarget.textContent = data.title || "Unknown Title"
    this.authorDisplayTarget.textContent = data.authors?.[0] || data.author || "Unknown Author"
    this.publisherDisplayTarget.textContent = data.publisher || ""

    if (data.cover_image_url || data.thumbnail) {
      this.coverImageTarget.src = data.cover_image_url || data.thumbnail
      this.coverImageTarget.classList.remove("hidden")
    }

    this.hideLoading()
    this.showPreview()
  }

  clear() {
    // Clear hidden fields
    this.titleTarget.value = ""
    this.subtitleTarget.value = ""
    this.authorTarget.value = ""
    this.isbnTarget.value = ""
    this.coverUrlTarget.value = ""
    this.descriptionTarget.value = ""
    this.pageCountTarget.value = ""
    this.publishedDateTarget.value = ""
    this.publisherTarget.value = ""

    // Clear input
    this.searchInputTarget.value = ""

    this.hidePreview()
  }

  showLoading() {
    this.searchSectionTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("hidden")
    this.previewTarget.classList.add("hidden")
  }

  hideLoading() {
    this.loadingTarget.classList.add("hidden")
    this.searchSectionTarget.classList.remove("hidden")
  }

  showPreview() {
    this.searchSectionTarget.classList.add("hidden")
    this.previewTarget.classList.remove("hidden")
  }

  hidePreview() {
    this.previewTarget.classList.add("hidden")
    this.searchSectionTarget.classList.remove("hidden")
  }
}
